import {
  Badge,
  Button,
  EmptyState,
  MoneyText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@paguz/ui';
import { useQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import { ArrowLeftIcon, ArrowRightIcon, WalletIcon, StoreIcon } from 'lucide-react';
import { api, queryKeys } from '../../api/endpoints.ts';
import {
  CopyValue,
  DataList,
  ErrorState,
  LoadingRows,
  PageHeader,
  Panel,
} from '../../components/page.tsx';
import { formatDateTime } from '../../lib/format.ts';
import { PAYMENT_STATUS, PHASE_LABEL } from '../../lib/labels.ts';
import { CUSTOMER_STATUS } from './customers-page.tsx';
import { LineFigures, LineStatusBadge, UsageBar } from './line-summary.tsx';

const route = getRouteApi('/app/clientes/$customerId');

export function CustomerPage() {
  const { customerId } = route.useParams();
  const query = useQuery({
    queryKey: queryKeys.customer(customerId),
    queryFn: () => api.getCustomer(customerId),
  });

  return (
    <>
      <Link
        to="/clientes"
        search={{}}
        className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
      >
        <ArrowLeftIcon className="size-4" /> Clientes
      </Link>
      {query.isPending ? (
        <LoadingRows rows={8} />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <CustomerDetail customer={query.data} />
      )}
    </>
  );
}

function CustomerDetail({
  customer,
}: {
  customer: NonNullable<Awaited<ReturnType<typeof api.getCustomer>>>;
}) {
  const status = CUSTOMER_STATUS[customer.status];
  return (
    <>
      <PageHeader
        eyebrow={<CopyValue value={customer.id} label="ID del cliente" />}
        title={customer.fullName}
        description={`${customer.businessName ?? 'Sin negocio registrado'} · ${customer.city ?? ''}`}
        actions={<Badge variant={status.tone}>{status.label}</Badge>}
      />

      <div className="grid gap-6 xl:grid-cols-[22rem_minmax(0,1fr)]">
        <div className="flex flex-col gap-6">
          <Panel title="Datos del cliente" description="PII enmascarada según tu rol.">
            <DataList
              columns={1}
              items={[
                {
                  label: 'Documento',
                  value: `${customer.documentType} ${customer.maskedDocument}`,
                },
                { label: 'Celular', value: customer.maskedPhone },
                { label: 'Correo', value: customer.email ?? '—' },
                { label: 'Cliente desde', value: formatDateTime(customer.createdAt) },
              ]}
            />
          </Panel>
          <Panel title="Cuentas de depósito vinculadas">
            <ul className="flex flex-col gap-3 text-sm">
              {customer.linkedAccounts.map((account) => (
                <li key={account.id} className="flex items-center justify-between gap-2">
                  <span>
                    <span className="font-semibold">{account.participantName}</span>
                    <span className="block text-muted-foreground">
                      {account.accountType} {account.maskedNumber}
                    </span>
                  </span>
                  {account.isDefault && <Badge variant="secondary">Principal</Badge>}
                </li>
              ))}
            </ul>
          </Panel>
          <Panel title="Comercios frecuentes">
            {customer.frequentPayees.length === 0 ? (
              <p className="text-sm text-muted-foreground">Aún no tiene comercios frecuentes.</p>
            ) : (
              <ul className="flex flex-col gap-3 text-sm">
                {customer.frequentPayees.map((payee) => (
                  <li key={payee.name} className="flex items-start gap-3">
                    <StoreIcon className="mt-0.5 size-4 text-primary" />
                    <span>
                      <span className="font-semibold">{payee.name}</span>
                      <span className="block text-muted-foreground">
                        {payee.nickname ? `“${payee.nickname}” · ` : ''}
                        {payee.count} compras
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>

        <div className="flex flex-col gap-6">
          {customer.lines.length === 0 ? (
            <EmptyState
              icon={<WalletIcon />}
              title="Sin línea de crédito"
              description={
                customer.activeApplication
                  ? `Tiene una solicitud en curso (fase ${PHASE_LABEL[customer.activeApplication.phase] ?? customer.activeApplication.phase}).`
                  : 'Todavía no ha solicitado un cupo.'
              }
            />
          ) : (
            customer.lines.map((line) => (
              <Panel
                key={line.id}
                title={line.productName}
                description={<CopyValue value={line.id} label="ID de la línea" />}
                actions={
                  <>
                    <LineStatusBadge line={line} />
                    <Button asChild size="sm" variant="outline">
                      <Link to="/lineas/$lineId" params={{ lineId: line.id }} search={{}}>
                        Ver línea <ArrowRightIcon />
                      </Link>
                    </Button>
                  </>
                }
              >
                <LineFigures line={line} />
                <div className="mt-5">
                  <UsageBar line={line} />
                </div>
              </Panel>
            ))
          )}

          <Panel title="Compras recientes" bodyClassName="p-0">
            {customer.recentPayments.length === 0 ? (
              <p className="p-5 text-sm text-muted-foreground">Sin compras todavía.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Comercio</TableHead>
                    <TableHead className="text-right">Monto</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customer.recentPayments.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="text-muted-foreground">
                        {formatDateTime(p.createdAt)}
                      </TableCell>
                      <TableCell>
                        <Link
                          to="/transacciones/$paymentIntentId"
                          params={{ paymentIntentId: p.id }}
                          className="font-semibold text-primary hover:underline"
                        >
                          {p.payeeName}
                        </Link>
                      </TableCell>
                      <TableCell className="text-right">
                        <MoneyText value={p.amount.amount} size="sm" />
                      </TableCell>
                      <TableCell>
                        <Badge variant={PAYMENT_STATUS[p.status].tone}>
                          {PAYMENT_STATUS[p.status].label}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Panel>

          <Panel title="Expedientes" bodyClassName="p-0">
            {customer.applications.length === 0 ? (
              <p className="p-5 text-sm text-muted-foreground">Sin expedientes.</p>
            ) : (
              <ul className="divide-y divide-border">
                {customer.applications.map((app) => (
                  <li
                    key={app.id}
                    className="flex items-center justify-between gap-3 px-5 py-3 text-sm"
                  >
                    <span>
                      <code className="font-mono text-xs">{app.id}</code>
                      <span className="block text-muted-foreground">
                        Solicitó <MoneyText value={app.requestedAmount.amount} size="sm" /> ·{' '}
                        {formatDateTime(app.createdAt)}
                      </span>
                    </span>
                    <Badge variant={app.status === 'IN_PROGRESS' ? 'highlight' : 'secondary'}>
                      {PHASE_LABEL[app.phase] ?? app.phase}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      </div>
    </>
  );
}
